import type { FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider, type AppCheck } from 'firebase/app-check';

let appCheckInstance: AppCheck | null = null;

/** Set when App Check is initialized from client config (reCAPTCHA v3 site key). */
export function getInitializedAppCheck(): AppCheck | null {
  return appCheckInstance;
}

declare global {
  // eslint-disable-next-line no-var
  var FIREBASE_APPCHECK_DEBUG_TOKEN: string | boolean | undefined;
}

/**
 * When Firebase App Check enforcement is on for Cloud Storage, uploads return 403 unless the web
 * app registers a provider. Add `appCheckRecaptchaV3SiteKey` to `clientConfigs/{clientId}` (reCAPTCHA
 * v3 key from Firebase Console → App Check → Web apps).
 *
 * Localhost: debug tokens are enabled automatically in development. Reload once, copy the token from
 * the browser console into Firebase Console → App Check → Manage debug tokens (same project as localhost).
 */
export function initAppCheckFromClientConfig(app: FirebaseApp, rawClientConfig: Record<string, unknown>): void {
  const siteKey =
    typeof rawClientConfig.appCheckRecaptchaV3SiteKey === 'string'
      ? rawClientConfig.appCheckRecaptchaV3SiteKey.trim()
      : '';
  if (!siteKey) return;

  if (process.env.NODE_ENV === 'development') {
    const debugRaw = process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN?.trim();
    globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN =
      debugRaw && debugRaw !== 'true' ? debugRaw : true;
    // eslint-disable-next-line no-console
    console.info(
      '[App Check] Localhost debug token enabled. After reload, copy the token from the console into Firebase Console → App Check → Manage debug tokens (project must match localhost).',
    );
  }

  appCheckInstance = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}
